import { VideoProject } from "@/services/bedrock";

import { getStatusColumnId, VIDEO_PROJECT_STATUS_COLUMNS, VideoProjectStatusColumnId } from "./columns";

interface StatusChangeOptions {
  requiresQa?: boolean | null;
  currentUserId?: number;
}

const STATUS_COLUMN_ORDER: VideoProjectStatusColumnId[] = [
  "submitted_for_editing",
  "editing",
  "submitted_for_qa_review",
  "qa_review",
  "completed",
];

export function getBackwardStatusChange(
  fromStatus: VideoProject.Status,
  options?: StatusChangeOptions
): VideoProjectStatusChangeRequest | null {
  const fromColumn = getStatusColumnId(fromStatus);
  if (!fromColumn) return null;
  const fromIndex = STATUS_COLUMN_ORDER.indexOf(fromColumn);
  if (fromIndex <= 0) return null;

  for (let index = fromIndex - 1; index >= 0; index--) {
    const request = getVideoProjectEventForStatusChange(fromStatus, STATUS_COLUMN_ORDER[index], options);
    if (request) return request;
  }
  return null;
}

export function getValidStatusColumnIds(
  fromStatus: VideoProject.Status,
  options?: StatusChangeOptions
): VideoProjectStatusColumnId[] {
  return VIDEO_PROJECT_STATUS_COLUMNS.map(column => column.id).filter(
    columnId => getVideoProjectEventForStatusChange(fromStatus, columnId, options) !== null
  );
}

export interface VideoProjectStatusChangeRequest {
  event: VideoProject.Event;
  userId?: number;
}

export function getVideoProjectEventForStatusChange(
  fromStatus: VideoProject.Status,
  toColumnId: VideoProjectStatusColumnId,
  options?: StatusChangeOptions
): VideoProjectStatusChangeRequest | null {
  const fromColumn = getStatusColumnId(fromStatus);
  if (!fromColumn || fromColumn === toColumnId) {
    return null;
  }

  switch (toColumnId) {
    case "editing":
      if (fromStatus === VideoProject.Status.SUBMITTED_FOR_EDITING) {
        return { event: VideoProject.Event.START_EDITING, userId: options?.currentUserId };
      }
      if (fromStatus === VideoProject.Status.SUBMITTED_FOR_QA_REVIEW) {
        return { event: VideoProject.Event.RETURN_TO_EDITING };
      }
      if (fromStatus === VideoProject.Status.QA_REVIEW) {
        return { event: VideoProject.Event.REJECT_QA };
      }
      if (fromStatus === VideoProject.Status.READY_TO_POST || fromStatus === VideoProject.Status.SCHEDULED) {
        return { event: VideoProject.Event.REOPEN_EDITING };
      }
      return null;

    case "submitted_for_editing":
      if (fromStatus === VideoProject.Status.EDITING) {
        return { event: VideoProject.Event.STOP_EDITING };
      }
      return null;

    case "submitted_for_qa_review":
      if (fromStatus === VideoProject.Status.EDITING) {
        return { event: VideoProject.Event.SUBMIT_FOR_QA_REVIEW };
      }
      if (fromStatus === VideoProject.Status.QA_REVIEW) {
        return { event: VideoProject.Event.STOP_QA_REVIEW };
      }
      return null;

    case "qa_review":
      if (fromStatus === VideoProject.Status.SUBMITTED_FOR_QA_REVIEW) {
        return { event: VideoProject.Event.START_QA_REVIEW, userId: options?.currentUserId };
      }
      return null;

    case "completed":
      if (fromStatus === VideoProject.Status.EDITING) {
        return options?.requiresQa ? null : { event: VideoProject.Event.COMPLETE };
      }
      if (fromStatus === VideoProject.Status.QA_REVIEW) {
        return { event: VideoProject.Event.APPROVE_QA };
      }
      return null;

    default:
      return null;
  }
}
